// apps/web/src/components/stories/BriefingContextPanel.tsx
//
// KAN-661 U5 — the contextual panel a briefing click adds to story detail.
// (Spec R19, R21, R22, R23, R24, R25, R26, R27, R29; AE6, AE7.)
//
// ---------------------------------------------------------------------------
// ADDITIVE, NEVER SUBSTITUTIVE (R24)
// ---------------------------------------------------------------------------
//
// This panel is an extra reason to trust the page, not a replacement for any
// part of it. The synthesised summary, the confidence label, the source count
// and the per-source original links all stay exactly where they were and are
// rendered by the page whether or not this component returns anything. That is
// why the component's only failure mode is `null`: there is no degraded or
// placeholder state, because a missing context is not a missing anything — it
// is simply an ordinary story visit.
//
// ---------------------------------------------------------------------------
// TWO REGIONS, NOT ONE (R22)
// ---------------------------------------------------------------------------
//
// The two halves say different kinds of thing and carry different warranties.
// "Matches your current settings" is a fact about the reader's own preferences,
// computed server-side from what those preferences are RIGHT NOW. "Suggested
// actions" is CyberFind's synthesis of someone else's reporting, and carries the
// verification warning that says so. Folding them into one region would let the
// warranty on the second be read as covering the first, or worse, let the
// warning be skipped because it sat under a heading about the reader's settings.
//
// ---------------------------------------------------------------------------
// HONEST ABOUT *WHEN* (R19, R21)
// ---------------------------------------------------------------------------
//
// The briefing email said "Tracked vendor" at send time. This says what matches
// now. If the reader untracked that vendor in between, the two disagree — and
// the correct behaviour is to show the current truth and say plainly that it is
// current, rather than reconstruct a send-time history that was deliberately
// never persisted (spec §12 non-goals).
//
// ---------------------------------------------------------------------------
// TEXT-ONLY (R26, R27, R29)
// ---------------------------------------------------------------------------
//
// Every reason is a word, never a colour or an icon alone; the regions are
// plain landmarks with headings at the page's second level; and the panel adds
// no interactive element at all, so it cannot move focus, cannot appear in the
// tab order, and cannot change the keyboard path through the page.

import { useId } from 'react'
import type {
  BriefingMatchReason,
  BriefingStoryContext,
} from '@/hooks/briefing-story-context'

/** R16 / R25 — at most three suggested actions survive projection. */
const MAX_ACTIONS = 3

/** R4 — at most two current matches are shown. */
const MAX_MATCHES = 2

/** R5, R27 — the generic label vocabulary, identical to the email's. */
const MATCH_REASON_LABELS: Record<BriefingMatchReason, string> = {
  tracked_vendor: 'Tracked vendor',
  keyword_watch: 'Keyword watch',
  industry: 'Your industry',
  jurisdiction: 'Your jurisdiction',
}

export const BRIEFING_SYNTHESIS_WARNING =
  'CyberFind synthesis — verify against cited sources and your environment'

/**
 * Trim, drop the empties, deduplicate exact normalised strings, cap at three.
 *
 * The API already does exactly this (R16/R25) and this repeats it deliberately.
 * The rule that decides whether the region is VISIBLE has to be the same rule
 * that decides what it contains, and that rule has to live where the render
 * happens — otherwise a future API that returns `['  ']` produces a heading, a
 * synthesis warning and an empty list, which is the "structurally perfect and
 * functionally dead" shape this estate has already shipped once.
 */
function projectActions(raw: readonly string[] | null | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const candidate of raw ?? []) {
    if (typeof candidate !== 'string') continue
    const trimmed = candidate.trim()
    if (trimmed === '' || seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push(trimmed)
    if (out.length === MAX_ACTIONS) break
  }
  return out
}

export interface BriefingContextPanelProps {
  context: BriefingStoryContext | null
}

export function BriefingContextPanel({ context }: BriefingContextPanelProps) {
  const matchesHeadingId = useId()
  const actionsHeadingId = useId()

  const actions = projectActions(context?.suggestedActions)
  const matches = (context?.currentMatches ?? []).slice(0, MAX_MATCHES)

  // R17/R25: nothing to say, nothing rendered — no heading, no empty shell.
  if (!context || (actions.length === 0 && matches.length === 0)) return null

  return (
    // `data-ph-no-capture`: `displayValue` is one of the reader's own watch
    // values (R20). PostHog autocapture would otherwise lift it out of the DOM
    // as element text on any interaction anywhere inside this subtree.
    //
    // `data-sentry-block` (KAN-677): `data-ph-no-capture` means nothing to
    // Sentry. Replay is NOT integrated today; this is here so that enabling it
    // cannot record the panel. Block rather than `data-sentry-mask`: masking
    // keeps the DOM shape and each value's character count and skips attribute
    // values, whereas block swaps the whole subtree for an empty placeholder.
    // Pinned by scripts/check-replay-masking.mjs (checkSentryReplayTripwire).
    <div
      className="space-y-3"
      data-testid="briefing-context-panel"
      data-ph-no-capture
      data-sentry-block
    >
      {matches.length > 0 && (
        <section
          aria-labelledby={matchesHeadingId}
          data-testid="briefing-current-matches"
          className="rounded-md border border-border bg-muted/40 p-4"
        >
          <h2
            id={matchesHeadingId}
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Matches your current settings
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Computed from your current preferences, not from when the briefing was sent.
          </p>
          <ul className="mt-2 space-y-1">
            {matches.map((match) => (
              <li key={`${match.reason}:${match.displayValue}`} className="text-sm">
                <span className="text-muted-foreground">
                  {MATCH_REASON_LABELS[match.reason]}
                </span>{' '}
                <span className="font-medium">{match.displayValue}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {actions.length > 0 && (
        <section
          aria-labelledby={actionsHeadingId}
          data-testid="briefing-suggested-actions"
          className="rounded-md border border-border bg-muted/40 p-4"
        >
          <h2
            id={actionsHeadingId}
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Suggested actions
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">{BRIEFING_SYNTHESIS_WARNING}</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            {actions.map((action) => (
              <li key={action} className="text-sm">
                {action}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}
